import { useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { exportAlumni } from '../../api/alumni';
import { AlumniListParams } from '../../types/alumni';
import { downloadBlob } from '../../utils/downloadBlob';

/** Mirrors the server's export column catalogue (alumniExport.ts). */
export const EXPORT_COLUMNS: Array<{ key: string; label: string }> = [
  { key: 'alumniNumber', label: 'Alumni ID' },
  { key: 'fullName', label: 'Full name' },
  { key: 'phone', label: 'Contact number' },
  { key: 'email', label: 'Email' },
  { key: 'employer', label: 'Place of work' },
  { key: 'jobTitle', label: 'Job title' },
  { key: 'graduationYear', label: 'Graduation year' },
  { key: 'countryOfWork', label: 'Country of work' },
  { key: 'currentCity', label: 'Current city' },
  { key: 'status', label: 'Status' },
  { key: 'studentNumber', label: 'Student ID' },
  { key: 'university', label: 'University' },
  { key: 'course', label: 'Course / programme' },
  { key: 'intake', label: 'Intake' },
  { key: 'specialization', label: 'Specialization' },
  { key: 'previousEmployer', label: 'Previous employer' },
  { key: 'whatsappNumber', label: 'WhatsApp' },
  { key: 'alternativePhone', label: 'Alternative number' },
  { key: 'personalEmail', label: 'Personal email' },
  { key: 'workEmail', label: 'Work email' },
  { key: 'linkedinUrl', label: 'LinkedIn' },
  { key: 'furtherQualification', label: 'Further qualification' },
  { key: 'branch', label: 'Branch' },
  { key: 'lastVerifiedAt', label: 'Last verified' },
  { key: 'nextFollowUpAt', label: 'Next follow-up' },
  { key: 'notes', label: 'Notes' },
  { key: 'archived', label: 'Archived' },
];

export const DEFAULT_EXPORT_COLUMNS = ['alumniNumber', 'fullName', 'phone', 'email', 'employer', 'jobTitle', 'graduationYear', 'countryOfWork', 'status'];

/** Export the current filtered alumni list (Phase 105) to Excel, PDF or
 * CSV, with the columns ticked here. */
export const AlumniExportDialog = ({
  open,
  onClose,
  params,
  total,
}: {
  open: boolean;
  onClose: () => void;
  params: AlumniListParams;
  total: number | undefined;
}) => {
  const [format, setFormat] = useState<'xlsx' | 'pdf' | 'csv'>('xlsx');
  const [columns, setColumns] = useState<string[]>(DEFAULT_EXPORT_COLUMNS);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggle = (key: string) =>
    setColumns((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      const { blob, filename } = await exportAlumni({ ...params, format, columns });
      downloadBlob(blob, filename);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't export the alumni list");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm" aria-labelledby="alumni-export-title">
      <DialogTitle id="alumni-export-title">Export alumni</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Exports the {total ?? ''} record{total === 1 ? '' : 's'} matching your current search and filters.
          </Typography>
          <RadioGroup row value={format} onChange={(e) => setFormat(e.target.value as typeof format)}>
            <FormControlLabel value="xlsx" control={<Radio />} label="Excel" />
            <FormControlLabel value="pdf" control={<Radio />} label="PDF" />
            <FormControlLabel value="csv" control={<Radio />} label="CSV" />
          </RadioGroup>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle2">Columns</Typography>
            <Stack direction="row" spacing={1}>
              <Button size="small" onClick={() => setColumns(EXPORT_COLUMNS.map((c) => c.key))}>
                All
              </Button>
              <Button size="small" onClick={() => setColumns(DEFAULT_EXPORT_COLUMNS)}>
                Default
              </Button>
            </Stack>
          </Stack>
          <FormGroup sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
            {EXPORT_COLUMNS.map((c) => (
              <FormControlLabel
                key={c.key}
                control={<Checkbox size="small" checked={columns.includes(c.key)} onChange={() => toggle(c.key)} />}
                label={c.label}
              />
            ))}
          </FormGroup>
          {format === 'pdf' && columns.length > 10 && (
            <Alert severity="info">Many columns make a PDF hard to read — Excel suits wide exports better.</Alert>
          )}
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" disabled={busy || columns.length === 0} onClick={() => void run()}>
          {busy ? 'Exporting…' : 'Export'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
